// import { ContentfulPage } from '../types/graphql-types'
import { SideNav, SideNavLinks } from '../types/navigation.types'

type CompareValue = {
  sequence: number
  title: string
}

export type ExtendedSideNavLink = SideNavLinks & { sequence: number }
export type ExtendedSideNav = SideNav & { sequence: number; extendedLinks: ExtendedSideNavLink[] }

function comparator(a: CompareValue, b: CompareValue) {
  const diff = a.sequence - b.sequence
  if (diff !== 0) return diff
  return a.title.localeCompare(b.title)
}

function transformToSideNavLink({ id, title, url }: ExtendedSideNavLink) {
  return { id, title, url }
}

function transformToSideNav({ id, title, extendedLinks }: ExtendedSideNav): SideNav {
  return {
    id,
    title,
    links: extendedLinks.map(transformToSideNavLink),
  }
}

function transformSideNavRecord(rec: Record<string, ExtendedSideNav[]>): Record<string, SideNav[]> {
  const result: Record<string, SideNav[]> = {}
  Object.entries(rec).forEach(([type, sideNavs]) => {
    result[type] = sideNavs.map(transformToSideNav)
  })
  return result
}

export type SideNavProcessed = {
  sideNavsRecord: Record<string, SideNav[]>
  pageIdSandboxKeyMap: Record<string, string[]>
}

export default function getSideNavDataFromPages(
  nodes: ReadonlyArray<GatsbyTypes.sideNavFragment | Partial<GatsbyTypes.ContentfulPage>>,
): SideNavProcessed {
  const pageIdSandboxKeyMap: Record<string, string[]> = {}
  const sideNavRecord: Record<string, ExtendedSideNav[]> = {}
  const sideNavIdAndPagesMap = new Map<string, ExtendedSideNavLink[]>()

  nodes.forEach(node => {
    if (!node || !node.id || !node.fields?.slug || !node.type || !node.navigationTitle) {
      throw new Error('Invalid Node found')
    }
    const link: ExtendedSideNavLink = {
      id: node.id,
      title: node.navigationTitle,
      url: node.fields?.slug,
      sequence: node.sequence || 0,
    }

    pageIdSandboxKeyMap[node.id] = ((node as any).sandboxKeys || []).filter(Boolean) as string[]

    const sideNavGroup: ExtendedSideNav = node.group
      ? {
          id: node.group.id,
          title: node.group.groupName || '',
          sequence: node.group.sequence || 0,
          links: [],
          extendedLinks: [],
        }
      : {
          id: `no_group:${node.type}`,
          title: '',
          sequence: Number.MIN_SAFE_INTEGER,
          links: [],
          extendedLinks: [],
        }

    if (!Array.isArray(sideNavRecord[node.type])) {
      sideNavRecord[node.type] = []
    }

    if (!sideNavIdAndPagesMap.has(sideNavGroup.id)) {
      sideNavRecord[node.type].push(sideNavGroup)
      sideNavIdAndPagesMap.set(sideNavGroup.id, sideNavGroup.extendedLinks)
    }

    const links = sideNavIdAndPagesMap.get(sideNavGroup.id)
    links?.push(link)
  })

  Object.keys(sideNavRecord).forEach(type => {
    const groups = sideNavRecord[type]
    sideNavRecord[type] = groups
      .map(group => ({
        ...group,
        extendedLinks: group.extendedLinks.sort(comparator),
      }))
      .sort(comparator)
  })
  return { sideNavsRecord: transformSideNavRecord(sideNavRecord), pageIdSandboxKeyMap }
}
